import React, { useState, useEffect } from 'react';
import WmsSidebar from '../../components/WmsSidebar';
import WmsTopbar from '../../components/wms_topbar';
import ProductTable from '../../components/wms/ProductTable';
import ProductFilters from '../../components/wms/ProductFilters';
import ProductModal from '../../components/wms/ProductModal';
import ImportExportModal from '../../components/wms/ImportExportModal';
import ProductDetailsDrawer from '../../components/wms/ProductDetailsDrawer';
import Notification from '../../components/wms/Notification';
import { getWmsProductos, createWmsProducto, updateWmsProducto, deleteWmsProducto, getSiguienteSku } from '../../api.wms.productos';
import { getWmsClientes } from '../../api.wms.clientes';
import * as XLSX from 'xlsx';

// TODO: Cargar cuentas/clientes reales desde el backend si aplica
// const [clientes, setClientes] = useState([]);

const WmsProductos = () => {
  const [activeItem, setActiveItem] = useState('productos');
  const [showProductModal, setShowProductModal] = useState(false);
  const [showImportExport, setShowImportExport] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [notification, setNotification] = useState(null);
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [filtros, setFiltros] = useState({ busqueda: '' });
  const [productos, setProductos] = useState([]);
  const [clientes, setClientes] = useState([]); // Si tienes endpoint de cuentas, cárgalos aquí
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Estado para sidebar móvil
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    getWmsProductos()
      .then(data => setProductos(data))
      .catch(() => setProductos([]))
      .finally(() => setLoading(false));
    // Cargar clientes reales desde el backend
    getWmsClientes()
      .then(data => setClientes(data))
      .catch(() => setClientes([]));
  }, []);

  // Filtrado usando campos reales del modelo WMS
  const productosFiltrados = productos.filter(p =>
    (!clienteSeleccionado || p.cuenta_id === Number(clienteSeleccionado)) &&
    (!filtros.busqueda ||
      (p.codigo_producto && p.codigo_producto.toLowerCase().includes(filtros.busqueda.toLowerCase())) ||
      (p.descripcion && p.descripcion.toLowerCase().includes(filtros.busqueda.toLowerCase())) ||
      (p.familia && p.familia.toLowerCase().includes(filtros.busqueda.toLowerCase()))
    )
  );

  // Acciones CRUD
  const handleSaveProduct = async prod => {
    setLoading(true);
    try {
      if (prod.id) {
        await updateWmsProducto(prod.id, prod);
        setNotification({ type: 'success', message: 'Producto actualizado' });
      } else {
        await createWmsProducto(prod);
        setNotification({ type: 'success', message: 'Producto creado' });
      }
      const productosActualizados = await getWmsProductos();
      setProductos(productosActualizados);
    } catch (e) {
      setNotification({ type: 'error', message: 'Error al guardar producto' });
    }
    setLoading(false);
    setShowProductModal(false);
    setSelectedProduct(null);
  };

  const handleDeleteProduct = async prod => {
    if (window.confirm('¿Eliminar producto?')) {
      setLoading(true);
      try {
        await deleteWmsProducto(prod.id);
        setNotification({ type: 'success', message: 'Producto eliminado' });
        setProductos(await getWmsProductos());
      } catch (e) {
        setNotification({ type: 'error', message: 'Error al eliminar producto' });
      }
      setLoading(false);
    }
  };

  // Adaptar onImport para soportar preview y batch
  const handleImport = async (fileOrBatch, opts = {}) => {
    if (opts.previewOnly) {
      // PREVIEW: leer archivo, validar y mostrar resumen, pero NO crear en backend
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const productosExcel = XLSX.utils.sheet_to_json(worksheet);
            let errores = 0, detalles = [];
            // Validar y preparar productos
            // Permitir match por nombre o por ID de cliente en la columna Cuenta
            const clientesMap = Object.fromEntries(clientes.map(c => [String(c.nombre).trim().toLowerCase(), c.id]));
            const clientesIdSet = new Set(clientes.map(c => String(c.id)));
            const productosBatch = [];
            for (let i = 0; i < productosExcel.length; i++) {
              const row = productosExcel[i];
              let cuentaNombre = (row.cuenta || row.Cuenta || '').toString().trim();
              let cuenta_id = null;
              // Buscar por nombre (case-insensitive)
              if (cuentaNombre && clientesMap[cuentaNombre.toLowerCase()]) {
                cuenta_id = clientesMap[cuentaNombre.toLowerCase()];
              } else if (cuentaNombre && clientesIdSet.has(cuentaNombre)) {
                cuenta_id = Number(cuentaNombre);
              }
              if (!cuentaNombre || !cuenta_id) {
                errores++;
                detalles.push({ row: i + 2, error: 'Cuenta/Cliente es requerido o no existe (usa nombre o ID exacto)' });
                continue;
              }
              if (!row.Descripción && !row.descripcion) {
                errores++;
                detalles.push({ row: i + 2, error: 'Descripción es requerida' });
                continue;
              }
              // Limpiar campos igual que el modal
              const limpiarInt = v => v === '' ? null : parseInt(v, 10);
              const limpiarFloat = v => v === '' ? null : parseFloat(v);
              const limpiarStr = v => (v === '' || v === null || v === undefined) ? null : String(v);
              productosBatch.push({
                cuenta_id,
                descripcion: row.descripcion || row["Descripción"],
                peso_kg: limpiarFloat(row.peso_kg || row["Peso (kg)"] || ''),
                volumen_m3: limpiarFloat(row.volumen_m3 || row["Volumen (m3)"] || ''),
                familia: limpiarStr(row.familia || row.Familia || ''),
                unidad_medida: row.unidad_medida || row["Unidad"],
                activo: row.activo !== undefined ? row.activo : true,
                observaciones: limpiarStr(row.observaciones || row.Observaciones || ''),
                fecha_creacion: new Date().toISOString(),
                codigo_barras: limpiarStr(row.codigo_barras || row["Código de barras"] || ''),
                sku_cliente: limpiarStr(row.sku_cliente || row["SKU cliente"] || ''),
                color: limpiarStr(row.color || row["Color"] || ''),
                talla: limpiarStr(row.talla || row["Talla"] || ''),
                presentacion: limpiarStr(row.presentacion || row["Presentación"] || ''),
                capacidad: limpiarStr(row.capacidad || row["Capacidad"] || ''),
                modelo: limpiarStr(row.modelo || row["Modelo"] || ''),
                alto: limpiarStr(row.alto || row["Alto"] || ''),
                ancho: limpiarStr(row.ancho || row["Ancho"] || ''),
                largo: limpiarStr(row.largo || row["Largo"] || ''),
                piezas_por_tarima: limpiarInt(row.piezas_por_tarima || row["Piezas por tarima"] || ''),
                piezas_por_caja: limpiarInt(row.piezas_por_caja || row["Piezas por caja"] || ''),
                piezas_por_paquete: limpiarInt(row.piezas_por_paquete || row["Piezas por paquete"] || ''),
              });
            }
            resolve({ productos: productosBatch, errores, detalles });
          } catch (e) {
            reject(e);
          }
        };
        reader.readAsArrayBuffer(fileOrBatch);
      });
    } else if (opts.batch) {
      // BATCH: crear productos en backend uno a uno (o aquí puedes implementar un endpoint batch real)
      let count = 0, errores = 0, detalles = [];
      for (let i = 0; i < fileOrBatch.length; i++) {
        try {
          // Obtener SKU igual que el modal
          const codigo_producto = await getSiguienteSku();
          const producto = { ...fileOrBatch[i], codigo_producto };
          await createWmsProducto(producto);
          count++;
        } catch (e) {
          errores++;
          detalles.push({ row: i + 2, error: 'Error al crear producto (posible duplicado o error de datos)' });
        }
      }
      setProductos(await getWmsProductos());
      return { count, errores, detalles };
    }
  };

  // Exportación real de productos a Excel
  const handleExport = () => {
    try {
      const dataToExport = productos.map(p => ({
        Cuenta: p.cuenta,
        Código: p.codigo_producto, // Solo exportación
        Descripción: p.descripcion,
        Familia: p.familia,
        Unidad: p.unidad_medida,
        'Peso (kg)': p.peso_kg,
        'Volumen (m3)': p.volumen_m3,
        Activo: p.activo ? 'Sí' : 'No',
        Observaciones: p.observaciones || '',
        'Código de barras': p.codigo_barras || '',
        'SKU cliente': p.sku_cliente || '',
        Color: p.color || '',
        Talla: p.talla || '',
        Presentación: p.presentacion || '',
        Capacidad: p.capacidad || '',
        Modelo: p.modelo || '',
        Alto: p.alto || '',
        Ancho: p.ancho || '',
        Largo: p.largo || '',
        'Piezas por tarima': p.piezas_por_tarima || '',
        'Piezas por caja': p.piezas_por_caja || '',
        'Piezas por paquete': p.piezas_por_paquete || '',
      }));
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Productos');
      XLSX.writeFile(wb, `productos_wms_${new Date().toISOString().slice(0,10)}.xlsx`);
      setNotification({ type: 'success', message: 'Productos exportados a Excel' });
    } catch (e) {
      setNotification({ type: 'error', message: 'Error al exportar productos' });
    }
    setShowImportExport(false);
  };
  // Exportar plantilla de ejemplo para importación
  const handleExportTemplate = () => {
    const template = [
      {
        Cuenta: '',
        Descripción: '',
        Familia: '',
        Unidad: '',
        'Peso (kg)': '',
        'Volumen (m3)': '',
        Activo: 'Sí',
        Observaciones: '',
        'Código de barras': '',
        'SKU cliente': '',
        Color: '',
        Talla: '',
        Presentación: '',
        Capacidad: '',
        Modelo: '',
        Alto: '',
        Ancho: '',
        Largo: '',
        'Piezas por tarima': '',
        'Piezas por caja': '',
        'Piezas por paquete': '',
      },
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
    XLSX.writeFile(wb, 'plantilla_productos_wms.xlsx');
    setNotification({ type: 'success', message: 'Plantilla exportada' });
  };
  const handleSelectRow = id => {
    setSelectedRows(rows => rows.includes(id) ? rows.filter(r => r !== id) : [...rows, id]);
  };
  const handleSelectAll = () => {
    setSelectedRows(selectedRows.length === productosFiltrados.length ? [] : productosFiltrados.map(p => p.id));
  };
  const handleResetFilters = () => {
    setClienteSeleccionado('');
    setFiltros({ busqueda: '' });
  };

  return (
    <div className="flex">
      {/* Sidebar drawer solo en móvil */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
      <div
        className={`fixed z-50 inset-y-0 left-0 transition-transform duration-300 md:hidden ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ width: '256px', minHeight: '100vh' }}
      >
        <WmsSidebar activeItem={activeItem} setActiveItem={setActiveItem} />
      </div>
      {/* Sidebar estático solo en desktop, en el flujo normal */}
      <div className="hidden md:flex">
        <WmsSidebar activeItem={activeItem} setActiveItem={setActiveItem} />
      </div>
      <div className="flex-1 bg-gray-100 min-h-screen md:p-6">
        <WmsTopbar rightContent={
          <button
            className="md:hidden p-2 ml-2"
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menú"
            style={{ background: 'transparent', border: 'none', boxShadow: 'none' }}
          >
            <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="feather feather-menu"><line x1="3" y1="12" x2="25" y2="12"/><line x1="3" y1="6" x2="25" y2="6"/><line x1="3" y1="18" x2="25" y2="18"/></svg>
          </button>
        } />
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
          <ProductFilters
            clientes={clientes}
            clienteSeleccionado={clienteSeleccionado}
            onClientChange={setClienteSeleccionado}
            onFilter={setFiltros}
            filtros={filtros}
            onReset={handleResetFilters}
          />
          <div className="flex gap-2 mt-2 md:mt-0">
            <button className="btn-primary" onClick={() => { setSelectedProduct(null); setShowProductModal(true); }}>Nuevo producto</button>
            <button className="btn-secondary" onClick={() => setShowImportExport(true)}>Importar/Exportar</button>
            {/* Acciones masivas, etc */}
          </div>
        </div>
        <ProductTable
          productos={productosFiltrados}
          onEdit={producto => { setSelectedProduct(producto); setShowProductModal(true); }}
          onDelete={handleDeleteProduct}
          onShowDetails={producto => { setSelectedProduct(producto); setShowDetails(true); }}
          selectedRows={selectedRows}
          onSelectRow={handleSelectRow}
          onSelectAll={handleSelectAll}
          loading={loading}
        />
        {showProductModal && (
          <ProductModal
            producto={selectedProduct}
            onClose={() => { setShowProductModal(false); setSelectedProduct(null); }}
            onSave={handleSaveProduct}
            clientes={clientes}
          />
        )}
        {showImportExport && (
          <ImportExportModal
            onClose={() => setShowImportExport(false)}
            onImport={handleImport}
            onExport={handleExport}
            onExportTemplate={handleExportTemplate}
          />
        )}
        {showDetails && (
          <ProductDetailsDrawer
            producto={selectedProduct}
            onClose={() => setShowDetails(false)}
            clientes={clientes}
          />
        )}
        {notification && (
          <Notification {...notification} onClose={() => setNotification(null)} />
        )}
      </div>
    </div>
  );
};

export default WmsProductos;
